import React, { useCallback, useContext, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { DataRoomSheet } from 'router/subrouters/DataRoom/models/DataRoom';
import ReorderResizeTable from 'components/ReorderResizeTable';
import {
  checkAreAllRequiredFieldsValid,
  checkHasEveryRequiredColumn,
  checkIfColumnIsValid,
  getInvalidDataRoomsCount,
  getInvalidOptionalDataRoomsCount,
  prepareValuesForApi,
} from './components/utils';
import { Loader } from 'ncoded-component-library';
import api from 'api';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import useCallbackRef from 'hooks/useCallbackRef';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import BulkAploadActions from 'components/BulkAploadActions';
import UpdateDataRoomDrawer from '../UpdateDataRoomDrawer';
import {
  BODY_CONTENT_GENERATE,
  HEADER_CONTENT_GENERATE,
  KeyCell,
  KeyHeader,
} from './components';
import useCallbackAsync from 'hooks/useCallbackAsync';
import BulkUploadValidationBanner from 'router/subrouters/Dash/subrouters/private/subrouters/AdminDashboard/subrouters/Enterprises/subrouters/Enterprise/pages/ManagePeople/components/BulkUploadValidationBanner';
import useDrawerNavigation from 'hooks/useDrawerNavigation';

import './BulkAddDataRooms.styles.scss';

type BulkAddDataRoomsProps = {
  className?: string;
  enterpriseId: string;
  draftDataRooms: DataRoomSheet[];
  columns: string[];
  onUpdateColumn: (key: string, newValue: string) => void;
  editDataRoom: (dataRoom: DataRoomSheet) => void;
  clearDataRooms: () => void;
};

const BulkAddDataRooms: React.FC<BulkAddDataRoomsProps> = (props) => {
  const {
    className,
    enterpriseId,
    columns,
    draftDataRooms,
    onUpdateColumn,
    editDataRoom,
    clearDataRooms,
  } = props;

  const [selectedDataRoom, setSelectedDataRoom] = useState<DataRoomSheet>();

  const [manageDRDrawer, manageDRDrawerRef] = useCallbackRef<SideDrawerRef>();

  const { popSuccess } = useContext(PopNotificationsContext);

  const { t } = useTranslation();

  const classes = classNames('bulk-add-data-rooms', 'white-panel', className);

  const count = useMemo(() => draftDataRooms?.length, [draftDataRooms?.length]);

  const numberOfProcessableColumns = useMemo(
    () => columns.filter(checkIfColumnIsValid).length,
    [columns],
  );

  const numberOfUnProcessableColumns = useMemo(
    () => columns.length - numberOfProcessableColumns,
    [columns, numberOfProcessableColumns],
  );

  const canSubmitContacts = useMemo(
    () =>
      !!count &&
      checkHasEveryRequiredColumn(columns) &&
      checkAreAllRequiredFieldsValid(draftDataRooms),
    [columns, count, draftDataRooms],
  );

  const invalidDataRoomsCount = useMemo(
    () => getInvalidDataRoomsCount(draftDataRooms),
    [draftDataRooms],
  );

  const invalidOptionalDataRoomsCount = useMemo(
    () => getInvalidOptionalDataRoomsCount(draftDataRooms),
    [draftDataRooms],
  );

  const handleEditDataRoom = useCallback(
    (contact: DataRoomSheet) => {
      setSelectedDataRoom(contact);
      manageDRDrawer.setOpen(true);
    },
    [manageDRDrawer],
  );

  const { onPrev, onNext } = useDrawerNavigation({
    rows: draftDataRooms,
    selectedId: selectedDataRoom?._id,
    onNavigate: handleEditDataRoom,
  });

  const handleClearDataRooms = useCallback(() => {
    setSelectedDataRoom(null);
    clearDataRooms();
  }, [clearDataRooms]);

  const renderedContent = useMemo(
    () =>
      columns.reduce(
        (acc, column) => ({
          ...acc,
          [column]: (dataRoom: DataRoomSheet) => {
            if (checkIfColumnIsValid(column))
              return BODY_CONTENT_GENERATE[column]({
                dataRoom,
                column,
              });

            return <KeyCell column={column} dataRoom={dataRoom} />;
          },
        }),
        {},
      ),
    [columns],
  );

  const renderedHeader = useMemo(
    () =>
      columns.reduce(
        (acc, column, index) => ({
          ...acc,
          [column]: () => {
            if (checkIfColumnIsValid(column))
              return HEADER_CONTENT_GENERATE[column]({
                column,
                index,
                allColumns: columns,
                onUpdateColumn,
              });

            return (
              <KeyHeader
                column={column}
                index={index}
                allColumns={columns}
                onUpdateColumn={onUpdateColumn}
              />
            );
          },
        }),
        {},
      ),
    [columns, onUpdateColumn],
  );

  const [handleBulkUpload, isLoading] = useCallbackAsync(async () => {
    await api.dataRoom.access.bulkCreateDataRoom(
      enterpriseId,
      prepareValuesForApi(draftDataRooms),
    );
    popSuccess(
      t('drImportedSuccesfully', {
        count,
      }),
    );
    clearDataRooms();
  });

  return (
    <div className={classes}>
      {isLoading && <Loader className="fixed" />}

      <h2>{t('bulkUploadDataRooms', { count })}</h2>

      <BulkUploadValidationBanner
        invalidMembersCount={invalidDataRoomsCount}
        invalidOptionalMembersCount={invalidOptionalDataRoomsCount}
      />

      <div className="bulk-add-data-rooms__scroll">
        <ReorderResizeTable
          columns={columns}
          isDragDisabled
          isResizeDisabled
          data={draftDataRooms}
          hasHorizontalScrollFade={false}
          tableContentTransformations={renderedContent}
          tableHeaderContentTransformations={renderedHeader}
          onRowClick={handleEditDataRoom}
        />
      </div>

      <BulkAploadActions
        title={t('uploadDataRooms', { count })}
        isDisabled={!canSubmitContacts}
        numberOfProcessableColumns={numberOfProcessableColumns}
        numberOfUnProcessableColumns={numberOfUnProcessableColumns}
        onClear={handleClearDataRooms}
        handleBulkUpload={handleBulkUpload}
      />

      <UpdateDataRoomDrawer
        ref={manageDRDrawerRef}
        dataRoom={selectedDataRoom}
        updateDataRoom={editDataRoom}
        onClose={() => setSelectedDataRoom(null)}
        onPrev={onPrev}
        onNext={onNext}
      />
    </div>
  );
};

export default BulkAddDataRooms;
